import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MapPin, Plus, X } from "lucide-react";

// Location chips for one staff member: add (capacity checked server-side) / remove (frees that location's seat).
export function StaffLocations({ u, locations, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [pick, setPick] = useState("");
  const assigned = new Set((u.locations || []).map((l) => l.id));
  const options = (locations || []).filter((l) => l.status === "active" && !assigned.has(l.id));
  const add = async () => {
    if (!pick) return;
    try { await api.post(`/users/${u.id}/locations`, { location_id: pick }); toast.success("Location assigned"); setAdding(false); setPick(""); onChanged(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(d?.code === "staff_limit" ? d.message : apiError(d)); }
  };
  const remove = async (l) => {
    try { await api.delete(`/users/${u.id}/locations/${l.id}`, { params: { reason: "removed by owner" } }); toast.success(`Removed from ${l.name}`); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (!locations?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid={`staff-locations-${u.id}`}>
      <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
      {(u.locations || []).length === 0 && <span className="text-[11px] text-amber-600" data-testid={`staff-unassigned-${u.id}`}>No location assigned</span>}
      {(u.locations || []).map((l) => (
        <Badge key={l.id} variant="outline" className="text-[10px] gap-1 pr-1" data-testid={`staff-loc-chip-${u.id}-${l.id}`}>{l.name}
          <button type="button" onClick={() => remove(l)} className="rounded hover:bg-secondary p-0.5" title={`Remove from ${l.name}`} data-testid={`staff-loc-remove-${u.id}-${l.id}`}><X className="w-3 h-3" /></button></Badge>
      ))}
      {!adding && options.length > 0 && <button type="button" onClick={() => { setAdding(true); setPick(options[0].id); }} className="text-[11px] text-emerald-600 hover:underline flex items-center" data-testid={`staff-loc-add-${u.id}`}><Plus className="w-3 h-3" /> location</button>}
      {adding && (
        <span className="flex items-center gap-1">
          <select className="h-7 rounded-md border border-input bg-background px-2 text-xs" value={pick} onChange={(e) => setPick(e.target.value)} data-testid={`staff-loc-select-${u.id}`}>
            {options.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.staff_used}/{l.staff_limit})</option>)}
          </select>
          <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={add} data-testid={`staff-loc-confirm-${u.id}`}>Add</Button>
          <Button size="sm" variant="ghost" className="h-7" onClick={() => setAdding(false)}>Cancel</Button>
        </span>
      )}
    </div>
  );
}
